// React
import { memo } from "react";

// Helpers
import { transformDateToLocalDateString } from "helpers";

// Servisni sloj
import { formatPricingValue, isMoneyKey } from "services/api/pricing/keyOptions";

// Tipovi
import { OverrideSummaryCardProps } from "../../types";

/**
 * Lijeva ploha čarobnjaka za poseban uvjet.
 *
 * Nosi živo razlaganje s poslužitelja: standardna vrijednost, ona nakon grupe i konačna uz ovaj
 * uvjet. Predstavnik tako vidi ishod dok tipka, umjesto da sam množi postotke.
 *
 * Razlaganje stiže kao goli brojevi, pa se jedinica uzima iz ključa. Bez toga bi probni period
 * od 14 dana pisao „14,00 €".
 */
const OverrideSummaryCard: React.FC<OverrideSummaryCardProps> = (
  props: OverrideSummaryCardProps
) => {
  const { formValues, tenantName, keyOption, preview } = props;

  const isMoney = isMoneyKey(keyOption);
  const valueKind = keyOption?.valueKind ?? "Money";

  const format = (value: number): string => formatPricingValue(value, valueKind);

  const outcomeLabel = () => {
    if (preview === null) return null;

    switch (preview.outcome) {
      case "Forbidden":
        return "Nije dopušteno";
      case "NeedsApproval":
        return "Traži odobrenje";
      default:
        return "Prolazi bez odobrenja";
    }
  };

  // Grupa smije dirati i ove ključeve, ali najčešće ne dira — stupac se tada ne prikazuje.
  const hasSchemeEffect = preview !== null && preview.afterScheme !== preview.standard;

  return (
    <div className="bg-blue-600 h-full w-full min-w-0 max-w-full overflow-hidden flex flex-col items-center justify-center p-8">
      <div className="text-center">
        <h1 className="text-4xl font-bold tracking-tight text-white">
          {tenantName ?? "Novi poseban uvjet"}
        </h1>

        {keyOption && (
          <div className="mt-4 inline-block rounded-full bg-white/15 px-4 py-1.5 text-sm text-white">
            {keyOption.name}
          </div>
        )}

        {formValues.validFrom && formValues.validTo && (
          <p className="text-blue-100 text-sm mt-3">
            Vrijedi od {transformDateToLocalDateString(new Date(formValues.validFrom))} do{" "}
            {transformDateToLocalDateString(new Date(formValues.validTo))}
          </p>
        )}
      </div>

      <div className="mt-8 w-full min-w-0 max-w-full overflow-x-auto overflow-y-hidden">
        {preview === null ? (
          <p className="text-blue-100 text-sm text-center">
            Odaberite klijenta, ključ i iznos, pa se ovdje pokazuje što bi se promijenilo.
          </p>
        ) : (
          <div className="w-max mx-auto flex divide-x divide-slate-200 bg-white rounded-2xl overflow-hidden shadow-2xl ring-1 ring-black/5">
            <div className="w-[200px] p-6">
              <div className="text-[13px] text-slate-500 min-h-[36px]">Iz cjenika</div>

              <div className="mt-2 text-[26px] leading-none font-bold text-slate-900">
                {format(preview.standard)}
              </div>
            </div>

            {(isMoney || hasSchemeEffect) && (
              <div className="w-[200px] p-6">
                <div className="text-[13px] text-slate-500 min-h-[36px]">Nakon grupe</div>

                <div className="mt-2 text-[26px] leading-none font-bold text-slate-900">
                  {format(preview.afterScheme)}
                </div>
              </div>
            )}

            <div className="w-[200px] p-6">
              <div className="text-[13px] text-slate-500 min-h-[36px]">
                Uz ovaj uvjet
              </div>

              <div className="mt-2 text-[26px] leading-none font-bold text-slate-900">
                {format(preview.effective)}
              </div>

              {/* Popust i marža su pojmovi cijene; kod dana i komada bi bili besmislica. */}
              {isMoney && (
                <div className="mt-2 text-[12px] text-slate-400">
                  ukupno {preview.discountPercent.toFixed(1).replace(".", ",")} %
                  {preview.margin !== null &&
                    `, marža ${(preview.margin * 100).toFixed(1).replace(".", ",")} %`}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Ishod odlučuje smije li se spremiti, pa stoji uz brojke, a ne tek na kraju. */}
        {preview !== null && (
          <div className="mt-6 text-center">
            <span className="inline-block rounded-full bg-white/15 px-4 py-1.5 text-sm text-white">
              {outcomeLabel()}
            </span>

            {preview.outcome === "Forbidden" && (
              <p className="text-blue-50 text-sm mt-3 max-w-md mx-auto">
                Efektivna cijena ne bi bila iznad nabave. To se ne odobrava ni uz suglasnost
                voditelja, pa se ovakav uvjet ne može spremiti.
              </p>
            )}

            {preview.outcome === "NeedsApproval" && (
              <p className="text-blue-50 text-sm mt-3 max-w-md mx-auto">
                Spremit će se, ali se neće primjenjivati dok ga netko ne odobri.
              </p>
            )}

            {!isMoney && preview.outcome === "Allowed" && (
              <p className="text-blue-50 text-sm mt-3 max-w-md mx-auto">
                Za granice i probni period ne postoji prag odobrenja, pa ovakav uvjet prolazi
                bez obzira na iznos.
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default memo(OverrideSummaryCard);
